import React from 'react';
import styles from './AnnotationStylePicker.module.css';

/** Named colors to hex for <input type="color"> which requires #rrggbb */
const NAMED_TO_HEX = {
    black: '#000000',
    white: '#ffffff',
};

function toHexColor(v) {
    if (!v) return '#000000';
    if (typeof v === 'string' && v.startsWith('#')) return v;
    return NAMED_TO_HEX[v] ?? (v.startsWith('#') ? v : '#000000');
}

const DISPLAY_DEFAULTS = {
    strokeColor: '#000000',
    strokeWidth: 1,
    fillColor: '#ffffff',
    fillOpacity: 1,
    strokeOpacity: 1,
};

const FIELD_LABELS = {
    strokeColor: 'Stroke color',
    strokeWidth: 'Stroke width',
    fillColor: 'Fill color',
    fillOpacity: 'Fill opacity',
    strokeOpacity: 'Stroke opacity',
};

/**
 * Reusable style picker for annotation UIs. All properties are optional;
 * which controls are shown is determined by the `fields` prop.
 *
 * @param {Object} props
 * @param {Object} [props.value] — Partial style { strokeColor?, strokeWidth?, fillColor?, fillOpacity?, strokeOpacity? }
 * @param {Function} props.onChange — Called with a partial object containing only the keys for visible fields
 * @param {string[]} [props.fields] — Which controls to show. Default: ['strokeColor', 'strokeWidth']
 */
export function AnnotationStylePicker({ value = {}, onChange, fields = ['strokeColor', 'strokeWidth'] }) {
    const partial = (key, val) => {
        onChange({ [key]: val });
    };

    const renderField = (key) => {
        const label = FIELD_LABELS[key];
        switch (key) {
            case 'strokeColor':
            case 'fillColor': {
                const displayVal = toHexColor(value[key] ?? DISPLAY_DEFAULTS[key]);
                return (
                    <span key={key} className={styles.field}>
                        <label className={styles.label}>{label}</label>
                        <input
                            type="color"
                            className={styles.colorInput}
                            value={displayVal}
                            onChange={(e) => partial(key, e.target.value)}
                            aria-label={label}
                        />
                    </span>
                );
            }
            case 'strokeWidth': {
                const num = value[key] ?? DISPLAY_DEFAULTS.strokeWidth;
                return (
                    <span key={key} className={styles.field}>
                        <label className={styles.label}>{label}</label>
                        <input
                            type="number"
                            className={styles.numberInput}
                            min={0.5}
                            step={0.5}
                            value={num}
                            onChange={(e) => partial(key, parseFloat(e.target.value) || 0.5)}
                            aria-label={label}
                        />
                    </span>
                );
            }
            case 'fillOpacity':
            case 'strokeOpacity': {
                const num = value[key] ?? DISPLAY_DEFAULTS[key];
                return (
                    <span key={key} className={styles.field}>
                        <label className={styles.label}>{label}</label>
                        <input
                            type="number"
                            className={styles.numberInput}
                            min={0}
                            max={1}
                            step={0.1}
                            value={num}
                            onChange={(e) => partial(key, parseFloat(e.target.value))}
                            aria-label={label}
                        />
                    </span>
                );
            }
            default:
                return null;
        }
    };

    if (!fields || fields.length === 0) return null;

    return (
        <div className={styles.root}>
            {fields.map((key) => renderField(key))}
        </div>
    );
}

export default AnnotationStylePicker;
